import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CURRENCIES = {
  INR: { symbol: '₹', name: 'Indian Rupee', rate: 1 },
  USD: { symbol: '$', name: 'US Dollar', rate: 0.012 },
  EUR: { symbol: '€', name: 'Euro', rate: 0.011 },
  GBP: { symbol: '£', name: 'British Pound', rate: 0.0094 },
  AED: { symbol: 'AED', name: 'UAE Dirham', rate: 0.044 },
  SGD: { symbol: 'S$', name: 'Singapore Dollar', rate: 0.016 },
  AUD: { symbol: 'A$', name: 'Australian Dollar', rate: 0.018 },
  CAD: { symbol: 'C$', name: 'Canadian Dollar', rate: 0.016 },
  JPY: { symbol: '¥', name: 'Japanese Yen', rate: 1.78 },
  SAR: { symbol: 'SAR', name: 'Saudi Riyal', rate: 0.045 },
};

// Map country/location keywords to currencies
const LOCATION_CURRENCY_MAP = {
  'india': 'INR', 'delhi': 'INR', 'mumbai': 'INR', 'bangalore': 'INR', 'chennai': 'INR', 'hyderabad': 'INR', 'pune': 'INR', 'kolkata': 'INR',
  'united states': 'USD', 'usa': 'USD', 'new york': 'USD', 'san francisco': 'USD', 'chicago': 'USD', 'los angeles': 'USD',
  'united kingdom': 'GBP', 'london': 'GBP', 'manchester': 'GBP',
  'germany': 'EUR', 'france': 'EUR', 'spain': 'EUR', 'italy': 'EUR', 'netherlands': 'EUR', 'berlin': 'EUR', 'paris': 'EUR',
  'uae': 'AED', 'dubai': 'AED', 'abu dhabi': 'AED',
  'singapore': 'SGD',
  'australia': 'AUD', 'sydney': 'AUD', 'melbourne': 'AUD',
  'canada': 'CAD', 'toronto': 'CAD', 'vancouver': 'CAD',
  'japan': 'JPY', 'tokyo': 'JPY',
  'saudi arabia': 'SAR', 'riyadh': 'SAR',
};

const CurrencyContext = createContext();

export function CurrencyProvider({ children }) {
  const [currency, setCurrency] = useState('INR');
  const [rates, setRates] = useState(CURRENCIES);

  useEffect(() => {
    // Try to detect currency from timezone
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      const tzLower = tz.toLowerCase();
      if (tzLower.includes('america')) setCurrency('USD');
      else if (tzLower.includes('europe/london')) setCurrency('GBP');
      else if (tzLower.includes('europe')) setCurrency('EUR');
      else if (tzLower.includes('asia/dubai')) setCurrency('AED');
      else if (tzLower.includes('asia/singapore')) setCurrency('SGD');
      else if (tzLower.includes('australia')) setCurrency('AUD');
      else if (tzLower.includes('asia/tokyo')) setCurrency('JPY');
      else if (tzLower.includes('asia/kolkata') || tzLower.includes('asia/calcutta')) setCurrency('INR');
    } catch {
      // Default to INR
    }

    // Fetch live exchange rates (free API, fallback to static)
    fetchRates();
  }, []);

  const fetchRates = async () => {
    try {
      const res = await fetch('https://open.er-api.com/v6/latest/INR');
      if (res.ok) {
        const data = await res.json();
        if (data.rates) {
          const updated = { ...CURRENCIES };
          Object.keys(updated).forEach(code => {
            if (data.rates[code]) {
              updated[code] = { ...updated[code], rate: data.rates[code] };
            }
          });
          setRates(updated);
        }
      }
    } catch {
      // Use static fallback rates
    }
  };

  const convert = useCallback((amountInINR) => {
    if (!amountInINR || currency === 'INR') return amountInINR;
    const rate = rates[currency]?.rate || 1;
    return Math.round(amountInINR * rate * 100) / 100;
  }, [currency, rates]);

  const format = useCallback((amountInINR) => {
    if (!amountInINR && amountInINR !== 0) return '';
    const sym = rates[currency]?.symbol || '₹';
    const converted = convert(amountInINR);
    if (currency === 'INR') {
      return `${sym}${converted.toLocaleString('en-IN')}`;
    }
    return `${sym}${converted.toLocaleString()}`;
  }, [currency, rates, convert]);

  const detectFromLocation = useCallback((location) => {
    if (!location) return;
    const loc = location.toLowerCase();
    for (const [keyword, curr] of Object.entries(LOCATION_CURRENCY_MAP)) {
      if (loc.includes(keyword)) {
        setCurrency(curr);
        return;
      }
    }
  }, []);

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, convert, format, rates, detectFromLocation, availableCurrencies: Object.keys(CURRENCIES) }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  return useContext(CurrencyContext);
}

export function CurrencySelector({ className = '' }) {
  const { currency, setCurrency, rates } = useCurrency();
  return (
    <select
      value={currency}
      onChange={(e) => setCurrency(e.target.value)}
      className={`h-8 px-2 text-xs rounded-lg border border-slate-200 bg-white ${className}`}
      data-testid="currency-selector"
    >
      {Object.entries(rates).map(([code, { symbol, name }]) => (
        <option key={code} value={code}>{symbol} {code} — {name}</option>
      ))}
    </select>
  );
}
